import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ViewChild} from '@angular/core';
import { ChatMode, ChatUserData } from './chat-user.interface';
import { NgScrollbar } from 'ngx-scrollbar';
import { ChatService } from './chat.service';
import { MessageData, MessageUpdate } from './message.interface';
import {Observable, concatMap, defer, filter, finalize, interval, map, of, take, tap} from 'rxjs';
import { ChatEventData } from './chat-event.interface';
import { LOAD_PLACEHOLDER, PLACEHOLDER_CHATGPT, PLACEHOLDER_DATABRICKS } from './chat-placeholder';

@Component({
  selector: 'qm-chat[messageUser]',
  template: `

    <div class="chatContainer">

      <ng-container *ngIf="!hideHeader">
        <div class="chatHeader">
          <div class="chatHeaderUserInfo">
            <div class="flex flex-column mr-2">
              <p-avatar [image]="messageUser.avatar" size="large" shape="circle"></p-avatar>
            </div>
            <div class="flex flex-column">
              <qm-label [labelMessage]="messageUser.username" styleFont="bold" class="mb-1" [styleColor]="darkMode? 'white': 'black'"></qm-label>
              <qm-label [labelMessage]="messageUser.online? 'Disponível' : 'Offline'" sizeFont="small" [styleColor]="darkMode? 'white': 'black' "></qm-label>
            </div>
          </div>

          <div class="chatHeaderButtons">
            <qm-button [buttonType]="chatStyle" buttonIconAction="arrow-down" [onlyIcon]="true" [rounded]="true"
                                                (clickEvent)="scrollbarRef.scrollTo({ bottom: 0 })"></qm-button>
          </div>
        </div>
      </ng-container>

      <div [style.background-color]="this.backgroundColorChat" class="chatBody">
          <ng-scrollbar [ngClass]="scrollBarResolver()" #scrollbarRef>
            <qm-message [msgUser]="messageUser" [msgData]="message" [chatStyle]="chatStyle" [darkMode]="darkMode" *ngFor="let message of messageList" />
          </ng-scrollbar>
      </div>

      <div [style.background-color]="this.backgroundColorChat" class="chatFooter">
        <div class="grid">
          <div class="col">
            <qm-input (keyup.enter)="sendMessage()" [hideLabel]="true" [(ngModel)]="chatInput"></qm-input>
          </div>
          <div style="width: 50px" class="col-fixed flex align-items-center justify-content-center mt-1">
            <qm-button [buttonType]="chatStyle" [buttonIconAction]="processing? 'waiting': 'send'" [onlyIcon]="true"
            (clickEvent)="sendMessage();" [disabled]="invalid || processing" ></qm-button>
          </div>
        </div>
        <ng-container *ngIf="chatMode.ragApi">
        <div class="grid">
          <div class="flex justify-content-center align-items-center col-2">
            <qm-label sizeFont='normal' [styleColor]="darkMode? 'white': 'black'" styleFont="bold" labelMessage="Código do cliente"></qm-label>
          </div>
          <div class="col">
              <qm-input (inputEvent)="changeCommand($event)"
                        (blurEvent)="updateCommand()"
                        [hideLabel]="true" [(ngModel)]="clientCode" [disabled]="chatMode.readOnlyCode"></qm-input>
          </div>
        </div>
      </ng-container>
      </div>

    </div>

  `,
  styleUrls: ['./qm-chat.component.css']
})
export class QmChatComponent implements OnInit, OnDestroy {

  chatInput!: string;
  _clientCode!: string;
  messageList!: MessageData[];
  processing: boolean = false;
  invalid: boolean = false;

  @Input()
  chatStyle: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'help' | 'error' = 'primary';

  @Input()
  darkMode: boolean = false;

  @Input()
  hideHeader: boolean = false;

  @Input()
  backgroundColorChat!: string;

  @Input()
  messageUser!: ChatUserData;

  @Input()
  chatMode!: ChatMode;

  @Output()
  messageSentEvent = new EventEmitter<ChatEventData>();

  get clientCode(): string {
      return this._clientCode;
  }

  @Input() set clientCode(value: string) {
      this._clientCode = value;
      this.invalid = this._clientCode.length === 0;
  }

  @ViewChild("scrollbarRef") scrollbar!: NgScrollbar;

  private readonly scrollBarClass;

  constructor(public chatService: ChatService) {

    this.scrollBarClass = new Map<string, string>([
      ['primary', 'chatScrollBar--primary'],
      ['secondary', 'chatScrollBar--secondary'],
      ['success','chatScrollBar--success'],
      ['info','chatScrollBar--info'],
      ['warning','chatScrollBar--warning'],
      ['help','chatScrollBar--help'],
      ['error','chatScrollBar--error']
    ])

  }

  ngOnInit(): void {

    this.messageList = [];

    this.chatService.createdMessage$().pipe(
      filter(msg => msg !== undefined),
      tap(() => this.processing = true)
    ).subscribe((newMsg) => this.messageList.push(newMsg!));

    this.chatService.updatedMessage$()
        .pipe(
          filter(msg => msg !== undefined),
          concatMap(msg => of({messageValue: this.messageList.find(x => x.id === msg!.id), messageSent: msg})),
          concatMap(msg => this.placeHolderEffect(msg.messageValue, msg.messageSent)),
          concatMap(cv1 => (cv1.messageSent!.typing ? this.typingEffect(cv1.messageValue, cv1.messageSent) : this.assignmentEffect(cv1.messageValue, cv1.messageSent)).pipe(
            map(curr => ({messageValue: cv1.messageValue, messageSent: cv1.messageSent, currentValue: curr})),
            this.finalizeWithValue(cv1 => this.assignMessageStatus(cv1.messageValue, cv1.messageSent))
          ))
        )
        .subscribe();

      this.chatService.commandMessage$().pipe(
        filter(msg => msg !== undefined),
      ).subscribe((newMsg) => this.messageList.push(newMsg!));

  }

  ngOnDestroy(): void {
    this.chatService.createdMessage$().unsubscribe();
    this.chatService.updatedMessage$().unsubscribe();
    this.chatService.commandMessage$().unsubscribe();
  }


  finalizeWithValue<T>(callback: (value: T) => void) {
    return (source: Observable<T>) => defer(() => {
      let lastValue: T;
      return source.pipe(
        tap(value => lastValue = value),
        finalize(() => callback(lastValue)),
      )
    })
  }

  placeHolderEffect(currentMessage: MessageData | undefined, updatedMessage: MessageUpdate | undefined) {
      if (currentMessage!.state === 'start' && updatedMessage!.state !== 'start') {
         currentMessage!.content = "";
         currentMessage!.state = 'sending';
      }
      return of({messageValue: currentMessage, messageSent: updatedMessage});
  }

  typingEffect(currentMessage: MessageData | undefined, updatedMessage: MessageUpdate | undefined): Observable<string> {
    return interval(5).pipe(
      map(x => currentMessage!.content += updatedMessage!.content[x]),
      take(updatedMessage!.content.length)
    )
  }

  assignmentEffect(currentMessage: MessageData | undefined, updatedMessage: MessageUpdate | undefined): Observable<string> {
      if (updatedMessage!.state === 'error') {
        return of(currentMessage!.content = updatedMessage!.content)
      } else {
        return of(currentMessage!.content += updatedMessage!.content)
      }
  }

  assignMessageStatus(currentMessage: MessageData | undefined, updatedMessage: MessageUpdate | undefined) {
      currentMessage!.state = updatedMessage!.state;
      if (currentMessage!.state === 'error' || currentMessage!.state === 'finish') {
          this.processing = false;
      }
      this.scrollChat();
  }

  sendMessage() {
    if (this.chatInput !== "" && !this.processing && !this.invalid) {
      const PLACEHOLDER = this.chatMode.ragApi? PLACEHOLDER_DATABRICKS : PLACEHOLDER_CHATGPT;

      if (this.chatMode.ragApi && this.chatInput.startsWith(this.chatMode.commandCode)) {
        this.clientCode =  this.chatInput.substring(this.chatMode.commandCode.length);
        this.updateCommand();
      } else {
        var msgIdReply = this.chatService.createSenderMessage(this.chatInput, PLACEHOLDER)
        this.messageSentEvent.emit({id: msgIdReply, content: this.chatInput, clientCode: this.clientCode});
      }

      this.chatInput = "";
      this.scrollChat();
    }
  }

  changeCommand(event: any) {
      this.invalid = event.target.value.length === 0;
  }

  updateCommand() {
      if (this.clientCode !== "") {
          this.chatService.createCommandMessage(LOAD_PLACEHOLDER + this.clientCode.trim())
      }
  }

  scrollChat() {
    setTimeout(() => this.scrollbar.scrollTo({ bottom: 0 }));
  }

  public scrollBarResolver(): string {
    return this.scrollBarClass.get(this.chatStyle) || 'chatScrollBar--primary';
  }

}

